/*
 * ProductModal — extends Dawn's ModalDialog to drive the lightbox.
 *
 * Behaviour (v13):
 *   - Opens with the media item the user clicked (via openedBy.data-media-id)
 *     active, all others hidden by CSS (.product-media-modal__content
 *     > *:not(.active) { display: none }).
 *   - Prev/Next buttons (data-modal-prev / data-modal-next, rendered in
 *     snippets/product-media-modal.liquid) cycle the .active class through
 *     the modal's direct children that carry [data-media-id]. They do NOT
 *     wrap — first/last disable the corresponding button, matching Hanger.
 *   - Arrow Left / Arrow Right do the same when the modal is open.
 *   - The counter (data-modal-counter-current) is updated on every change.
 *   - Clicks on the nav buttons stopPropagation() so the media-modal
 *     pointerup-to-close handler in global.js (line 610) doesn't dismiss
 *     the modal when the user wants to navigate.
 *
 * Skipped:
 *   - Wrap-around is intentionally NOT supported; disabled state on the
 *     edge button is the visual cue. Matches the reference.
 *   - Touch swipe is handled by the inner img naturally on mobile where
 *     the modal content is full-width; revisit if merchant requests it.
 */
if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this._prevBtn = this.querySelector('[data-modal-prev]');
        this._nextBtn = this.querySelector('[data-modal-next]');
        this._counter = this.querySelector('[data-modal-counter-current]');

        if (this._prevBtn) {
          this._prevBtn.addEventListener('click', (e) => {
            // Prevent the inherited media-modal pointerup-to-close (global.js:610).
            e.stopPropagation();
            this.navigate(-1);
          });
          // Also stop pointerup directly — the close handler is on pointerup, not click.
          this._prevBtn.addEventListener('pointerup', (e) => e.stopPropagation());
        }
        if (this._nextBtn) {
          this._nextBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.navigate(1);
          });
          this._nextBtn.addEventListener('pointerup', (e) => e.stopPropagation());
        }

        // Keyboard navigation. Bind on the element itself; the ModalDialog
        // base class already binds 'keyup' for ESC handling, so adding
        // another keyup listener here composes cleanly.
        this.addEventListener('keyup', (event) => {
          if (!this.hasAttribute('open')) return;
          if (event.code === 'ArrowLeft') {
            event.preventDefault();
            this.navigate(-1);
          } else if (event.code === 'ArrowRight') {
            event.preventDefault();
            this.navigate(1);
          }
        });
      }

      hide() {
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      // Returns the modal's direct media children (one per product.media entry).
      // Filtering to direct children avoids matching the inner <img data-media-id>
      // that lives inside <deferred-media> wrappers for video/3D types.
      _getMediaItems() {
        const content = this.querySelector('.product-media-modal__content');
        if (!content) return [];
        return Array.from(content.children).filter((el) => el.hasAttribute('data-media-id'));
      }

      showActiveMedia() {
        const items = this._getMediaItems();
        if (items.length === 0) return;

        const targetId = this.openedBy && this.openedBy.getAttribute
          ? this.openedBy.getAttribute('data-media-id')
          : null;

        const activeIdx = Math.max(
          0,
          items.findIndex((el) => el.getAttribute('data-media-id') === targetId)
        );

        this._activate(items, activeIdx);
      }

      navigate(direction) {
        const items = this._getMediaItems();
        if (items.length < 2) return;

        const currentIdx = items.findIndex((el) => el.classList.contains('active'));
        if (currentIdx === -1) return;

        const newIdx = currentIdx + direction;
        if (newIdx < 0 || newIdx >= items.length) return;

        this._activate(items, newIdx);
      }

      _activate(items, idx) {
        items.forEach((item, i) => item.classList.toggle('active', i === idx));

        const active = items[idx];

        // Lazy-load deferred content (YouTube iframe etc.) when activated.
        if (active && active.nodeName === 'DEFERRED-MEDIA') {
          const tpl = active.querySelector('template');
          if (tpl && tpl.content && tpl.content.querySelector('.js-youtube')) {
            active.loadContent();
          }
        }

        // Update counter.
        if (this._counter) this._counter.textContent = idx + 1;

        // Update nav button disabled state.
        if (this._prevBtn) this._prevBtn.disabled = idx <= 0;
        if (this._nextBtn) this._nextBtn.disabled = idx >= items.length - 1;
      }
    }
  );
}
